
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import FilterMenu from '../filterMenu/FilterMenu';
import DevProject from './DevProject';
import DesProject from './DesProject';
import DevButton from '../button/DevButton';

import { featured_projects as devProjects } from '../../utils/devProjects';
import { featured_projects as desProjects } from '../../utils/desProjects';
import filterProjects, { devFilters, designFilters } from '../../utils/filterProjects';
import { fetchGithubProjects } from '../../utils/githubSync';

import { GlobeDemo } from '../ui/GlobeDemo';

const Projects = () => {
    const devMode = useSelector(state => state.devMode);
    const activeFilters = useSelector(state => state.activeFilters);
    const [filters, setFilters] = useState([]);

    const calculateProjectsPerPage = React.useCallback(() => {
        if (!devMode) {
            return 10;
        }
        return window.innerWidth >= 1600 ? 8 : 6;
    }, [devMode]);

    const [projectsPerPage, setProjectsPerPage] = useState(calculateProjectsPerPage());
    const [currentPage, setCurrentPage] = useState(0);
    const [projects, setProjects] = useState([]);
    const [visibleProjects, setVisibleProjects] = useState([]);
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [githubProjects, setGithubProjects] = useState([]);

    useEffect(() => {
        const handleResize = () => {
            setProjectsPerPage(calculateProjectsPerPage());
        }
        window.addEventListener('resize', handleResize);
        // Update on devMode change as well
        setProjectsPerPage(calculateProjectsPerPage());

        return () => window.removeEventListener('resize', handleResize);
    }, [devMode, calculateProjectsPerPage]);

    // Fetch github projects
    useEffect(() => {
        const getGithub = async () => {
            const projects = await fetchGithubProjects();
            setGithubProjects(projects);
        };
        getGithub();
    }, []);

    // (Re)Set current page
    useEffect(() => {
        setCurrentPage(0);
    }, [devMode, activeFilters]);

    // Set filters
    useEffect(() => {
        if (devMode) {
            const normalizeUrl = (url) => url ? url.replace(/\.git$/, '') : '';
            const staticMap = new Map(devProjects.map(p => [normalizeUrl(p.githubUrl), p]));

            const allDevProjects = githubProjects.map(ghProj => {
                const normalizedGh = normalizeUrl(ghProj.githubUrl);
                return staticMap.get(normalizedGh) || ghProj;
            });
            setFilters(devFilters(allDevProjects));
        } else {
            setFilters(designFilters(desProjects));
        }
    }, [devMode, githubProjects]);

    // Set Total (filtered) Projects
    useEffect(() => {
        let featuredProjects = devMode ? devProjects.map(proj => proj) : desProjects.map(proj => proj);

        if (devMode) {
            const normalizeUrl = (url) => url ? url.replace(/\.git$/, '') : '';
            const staticMap = new Map(featuredProjects.map(p => [normalizeUrl(p.githubUrl), p]));

            featuredProjects = githubProjects.map(ghProj => {
                const normalizedGh = normalizeUrl(ghProj.githubUrl);
                return staticMap.get(normalizedGh) || ghProj;
            });
        }

        const hasActiveFilters = activeFilters.length > 0;
        hasActiveFilters ? setProjects(filterProjects(featuredProjects, activeFilters)) : setProjects(featuredProjects);
    }, [activeFilters, devMode, githubProjects]);

    // Set Visible Projects
    useEffect(() => {
        const pageResults = projects.slice(currentPage, currentPage + projectsPerPage);
        if (currentPage === 0) {
            setVisibleProjects(pageResults)
        } else {
            setVisibleProjects((prevResults) => [...prevResults, ...pageResults])
        }
    }, [currentPage, projects, projectsPerPage]);

    // Show/Hide Load More Button 
    useEffect(() => {
        const totalProjects = projects.length;
        const totalVisibleProjects = visibleProjects.length;
        setHasMoreResults(totalProjects > totalVisibleProjects);
    }, [projects, visibleProjects]);

    const loadMoreProjects = () => {
        setCurrentPage((prevPage) => prevPage + projectsPerPage);
    }

    return (
        <section className={`projects page ${devMode ? 'dev' : 'des'}`} id='projects'>

            <h2>Projects</h2>
            <p className='filter-text'>Filter by:</p>
            <FilterMenu filters={filters} />

            <div className='projects-container'>
                {visibleProjects.map(proj => {
                    if (devMode) {
                        return (<DevProject project={proj} key={proj.key} />)
                    } else {
                        return (<DesProject project={proj} key={proj.key} />);
                    }
                })}
            </div>

            {hasMoreResults && (devMode ? (<DevButton
                className="load-more-btn dev"
                title="Load more projects"
                onClick={loadMoreProjects}
            >Load More</DevButton>) : (<button
                className="load-more-btn des"
                onClick={loadMoreProjects}
            >Load More</button>))}

            {devMode && <GlobeDemo />}
        </section>
    );
}

export default Projects;